<script lang="ts">
	import { Fileupload, Helper } from "flowbite-svelte";
	import { Indicator } from "flowbite-svelte-v1";
	import { getContext } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { v4 as uuidv4 } from "uuid";
	import { FileUploadPropertiesSchema, type Field, type FileUploadProperties } from "#gen/forms/v1/field_pb.js";
	import type { StaticAssetClient } from "#lib/api/static-assets.js";
	import Icon from "#lib/components/Icon.svelte";
	import FieldHeader from "#lib/components/page/components/form/FieldHeader.svelte";
	import type { TypeIcon } from "#lib/components/page/components/form/FieldProps.js";
	import { create } from "#lib/protobuf-compat.js";
	import Button from "#lib/ui/Button/index.js";
	import { ERR_REQUIRED } from "#lib/validation.js";

	type UploadedFile = { url: string; fileName?: string; mimetype?: string };

	interface Props {
		id: string;
		typeIcon?: TypeIcon;
		field: Field;
		value?: UploadedFile[] | string | string[] | null;
		editable?: boolean;
		active?: boolean;
		readOnly?: boolean;
		loading?: boolean;
		onChange?: (field: Field) => void;
		[key: string]: unknown;
	}

	let { typeIcon, field, value = $bindable(), editable = false, active = false, readOnly = false, loading = $bindable(false), onChange, ...rest }: Props = $props();

	let error: string | undefined = $state(undefined);

	const staticAssetClient: StaticAssetClient = getContext("staticAssetClient");
	const inputDisabled = $derived((editable && field.immutable) || readOnly || !field.active);
	const properties = $derived(field.properties?.properties.case === "fileUpload" ? (field.properties.properties.value as FileUploadProperties) : create(FileUploadPropertiesSchema));
	const captureImage = $derived(Boolean(properties.captureImage));
	const capturedImage = $derived(Array.isArray(value) && value.length > 0 && typeof value[0] === "object" ? value[0] : undefined);
	// Camera captures without a browser-supplied filename get a generated id with no extension, so show a friendly label for those.
	const displayName = $derived(capturedImage?.fileName && /\.[a-z0-9]+$/i.test(capturedImage.fileName) ? capturedImage.fileName : "Photo");

	export function validate(): boolean {
		if (loading) {
			return false;
		} else if (field.required && !value) {
			error = ERR_REQUIRED;
			return false;
		} else {
			error = "";
		}
		return true;
	}

	async function handleChange(e: Event) {
		try {
			const input = e.target as HTMLInputElement;
			if (!input?.files?.length) {
				return;
			}
			loading = true;
			error = "";
			const urls = await Promise.all(Array.from(input.files).map(file => staticAssetClient.upload(file)));
			value = urls.map((urlObj, i) => ({
				url: urlObj.url,
				fileName: generateImageName(input.files?.[i]?.name),
				mimetype: input.files?.[i]?.type,
			}));
			onChange?.(field);
		} catch (e) {
			error = e?.toString();
		} finally {
			loading = false;
		}
	}

	function generateImageName(name?: string) {
		if (!captureImage) {
			return name;
		}
		// Camera captures always get a UUID + timestamp title, ignoring the browser-supplied name (iOS names them "image.jpg").
		return `${uuidv4()}-${Date.now()}`;
	}

	function handleRemove(e: MouseEvent) {
		e.preventDefault();
		e.stopPropagation();
		value = undefined;
		onChange?.(field);
	}
</script>

<div {...rest}>
	{#if editable}
		<FieldHeader {typeIcon} {active} {field} on:change={e => onChange?.(e.detail)} />
	{:else}
		<label for={field.name} class="mb-2 inline-block text-sm font-medium" class:required={field.required}>
			{field.title}
		</label>
		{#if field.description}
			<div class="field-description my-2 text-xs text-gray-500">{field.description}</div>
		{/if}
	{/if}
	{#if captureImage}
		{#if capturedImage}
			<label for={field.name} class={twMerge("file-capture-target flex h-42 w-full cursor-pointer items-center justify-center gap-3 rounded-lg border bg-gray-50 p-2.5 text-sm", error ? "border-red-500" : "border-gray-300", (inputDisabled || loading) && "pointer-events-none cursor-not-allowed opacity-50")}>
				<div class="relative">
					<Indicator size="xl" placement="top-right" class="h-8 w-8">
						<Button color="none" onclick={handleRemove} data-action-name="remove-image" class="m-auto flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-gray-200 p-1">
							<Icon iconSet="heroicons" name="x-mark" class="h-5 w-5 text-gray-900" />
						</Button>
					</Indicator>
					<img src={capturedImage.url} alt={displayName} class="h-36 shrink-0 rounded object-cover" />
				</div>
			</label>
		{:else}
			<label for={field.name} class={twMerge("file-capture-target flex h-42 w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed bg-gray-50 p-0 text-sm font-medium focus-within:ring-1", error ? "border-red-500" : "border-gray-300", (inputDisabled || loading) && "pointer-events-none cursor-not-allowed opacity-50")}>
				<Icon name="camera" class="file-capture-target-icon mb-2 h-18 w-18" />
				<span class="file-capture-target-label">Take a photo</span>
			</label>
		{/if}
		<input id={field.name} name={field.name} type="file" accept="image/*" capture="environment" class="sr-only" onchange={handleChange} disabled={inputDisabled || loading} />
	{:else}
		<Fileupload class={twMerge("overflow-clip", readOnly && "disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100")} color={error ? "red" : "base"} on:change={handleChange} name={loading ? "..." : field.name} id={field.name} placeholder={loading ? "..." : "Upload a file!"} disabled={inputDisabled} />
	{/if}
	<div class="mt-2 flex items-center gap-2">
		{#if loading}
			<div class="flex items-center gap-2">
				<Icon name="arrow-path" class="form-label h-5 w-5 animate-spin" />
				<span class="form-label">Please wait for your {captureImage ? "image" : "file"} upload to be completed...</span>
			</div>
		{:else if error}
			<Helper color="red"><span class="font-medium">{error}</span></Helper>
		{/if}
	</div>
</div>
